/**
 * PeerProfileAvatar — peer-message-only profile thumbnail that hugs the left
 * edge of each peer bubble row.
 *
 * Flutter source of truth: `peerUserMessageProfileImage`
 * (`chat_screen.dart:2450-2476`).
 *
 * Behavior (matches Flutter line-for-line):
 *  - `isLastOfRun === true` AND peer is loaded → 20px circular peer avatar
 *    (`chat_screen.dart:2456-2472`).
 *  - `isLastOfRun === true` AND peer not yet fetched → 20×20 empty spacer
 *    (`chat_screen.dart:2453`).
 *  - `isLastOfRun === false` (peer message mid-run) → 20×20 spacer to keep
 *    every peer message indented by the same amount
 *    (`chat_screen.dart:2475`).
 *
 * The "last-of-run" computation lives in `MessageBubble.isLastMessageLeft`
 * (`MessageBubble.tsx:150-165`) — the caller (`MessageBubble`) passes the
 * resolved boolean through as `isLastOfRun` so this component stays a pure
 * renderer.
 *
 * This component is rendered by `MessageBubble` to the LEFT of every PEER
 * bubble (`crossAxisAlignment: end` in Flutter's `Row` at
 * `chat_screen.dart:2433-2444` → the avatar sits at the bottom-left of the
 * row). Own rows render `ReadReceiptAvatar` instead.
 *
 * Theme / responsive tokens:
 *  - Size                  → `responsive.icon.medium` (= 20, exact Flutter parity).
 *  - Border radius (round) → `responsive.borderRadius.round`.
 */

import React from 'react';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { CachedImage } from '@/presentation/components/common/CachedImage';
import { UserUtils } from '@/domain/utils/userUtils';
import { qaAttr, type QaAttrOptions } from '@/presentation/utils/qaAttr';
import type { UserEntity } from '@/domain/entities/user/userEntity';

export interface PeerProfileAvatarProps {
  /**
   * True iff this peer message is the last-of-run for the peer sender —
   * Flutter's `isLastMessageLeft` (`chat_screen.dart:727-738`). Only on
   * last-of-run do we show the avatar; mid-run bubbles get a spacer of the
   * same width so indentation stays consistent.
   */
  isLastOfRun: boolean;
  /** Peer user, or `null` if not yet fetched. */
  peerUser: UserEntity | null;
  /**
   * `data-qa-*` set for the avatar node. The caller (`MessageBubble`) builds
   * the id as `chat-peer-avatar-<userId>` so the qa-tester can find the peer
   * avatar. Spread onto the spacer too so the slot stays locatable.
   */
  qa?: QaAttrOptions;
}

export const PeerProfileAvatar: React.FC<PeerProfileAvatarProps> = ({
  isLastOfRun,
  peerUser,
  qa,
}) => {
  const responsive = useResponsiveDesign();
  const size = responsive.icon.medium;

  // Mid-run peer message OR peer not yet fetched → empty fixed slot
  // (`chat_screen.dart:2453` / `:2475`).
  if (!isLastOfRun || peerUser === null) {
    return (
      <div
        {...(qa ? qaAttr(qa) : {})}
        style={{ width: size, height: size, flexShrink: 0 }}
      />
    );
  }

  return (
    <CachedImage
      qaId={qa?.id}
      src={UserUtils.generateUserProfileImageUrl(peerUser.firebaseId)}
      alt=""
      style={{
        width: size,
        height: size,
        borderRadius: responsive.borderRadius.round,
        objectFit: 'cover',
        flexShrink: 0,
      }}
    />
  );
};
